import React from 'react';

export default function ProposalSummary({ proposalScenarios }) {
  // Safety check: ensure proposalScenarios is always an array
  const scenarios = proposalScenarios || [];
  
  // Filter scenarios with valid profitability results
  const scenariosWithResults = scenarios.filter(s => {
    const hasProfitabilityResults = s.profitability_results?.totals;
    const hasCalculationResults = s.calculation_results?.totals;
    return hasProfitabilityResults || hasCalculationResults;
  });
  
  // Normalize the data structure
  const normalizedScenarios = scenariosWithResults.map(s => ({
    ...s,
    profitability_results: s.profitability_results || s.calculation_results
  }));
  
  if (normalizedScenarios.length === 0) {
    return (
      <div className="card p-6 mb-6 bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 border-2 border-indigo-200">
        <h3 className="text-2xl font-bold text-indigo-900 mb-4">
          Proposal Summary
        </h3>
        <p className="text-slate-600">No profitability data available yet. Complete calculations with full data to see aggregated metrics.</p>
      </div>
    );
  }

  // Calculate aggregated totals across all services
  const aggregated = normalizedScenarios.reduce((acc, scenario) => {
    const totals = scenario.profitability_results?.totals || {};
    const netFees = scenario.profitability_results?.net_fees || {};
    const totalFees = totals.total_fees || 0;
    
    // Use backend's implied_tr directly (already excludes ACH correctly)
    const impliedTR = totals.implied_tr || 0;
    
    return {
      totalPayments: acc.totalPayments + (totals.total_payments || 0),
      totalFees: acc.totalFees + totalFees,
      totalCollections: acc.totalCollections + (totals.total_payments || 0),
      totalMerchantFees: acc.totalMerchantFees + (totals.merchant_fees || 0),
      totalNetRevenue: acc.totalNetRevenue + (totals.net_revenue || 0),
      totalReaderCosts: acc.totalReaderCosts + (netFees.reader_costs?.total_cost || 0),
      totalSimCosts: acc.totalSimCosts + (netFees.sim_costs?.total_cost || 0),
      totalSalesCommissions: acc.totalSalesCommissions + (netFees.sales_commissions?.total_cost || 0),
      totalPartnershipIncentive: acc.totalPartnershipIncentive + (netFees.partnership_incentive?.total_cost || 0),
      totalNetFees: acc.totalNetFees + (netFees.net_fees || 0),
      
      // Weighted averages using total_fees as weight (consistent with how TR is calculated)
      weightedImpliedTR: acc.weightedImpliedTR + (impliedTR * totalFees),
      weightedGPM: acc.weightedGPM + ((totals.gpm || 0) * totalFees),
      weightedNetProfitMargin: acc.weightedNetProfitMargin + ((netFees.net_fees_margin || 0) * totalFees),
    };
  }, {
    totalPayments: 0,
    totalFees: 0,
    totalCollections: 0,
    totalMerchantFees: 0,
    totalNetRevenue: 0,
    totalReaderCosts: 0,
    totalSimCosts: 0,
    totalSalesCommissions: 0,
    totalPartnershipIncentive: 0,
    totalNetFees: 0,
    weightedImpliedTR: 0,
    weightedGPM: 0,
    weightedNetProfitMargin: 0,
  });

  // Calculate weighted averages using total_fees as weight
  const avgImpliedTR = aggregated.totalFees > 0 
    ? aggregated.weightedImpliedTR / aggregated.totalFees 
    : 0;
  
  const avgGPM = aggregated.totalFees > 0 
    ? aggregated.weightedGPM / aggregated.totalFees 
    : 0;

  const avgNetProfitMargin = aggregated.totalFees > 0 
    ? aggregated.weightedNetProfitMargin / aggregated.totalFees 
    : 0;

  // Gross Profit = Total Fees - Merchant Fees
  const grossProfit = aggregated.totalFees - aggregated.totalMerchantFees;
  
  // Total Reader Costs (monthly + SIM annual)
  const totalReaderCosts = aggregated.totalReaderCosts + aggregated.totalSimCosts;

  // Debug logging
  console.log('📊 ProposalSummary:', {
    services: normalizedScenarios.length,
    avgImpliedTR: avgImpliedTR.toFixed(2) + '%',
    avgGPM: avgGPM.toFixed(2) + '%',
    avgNPM: avgNetProfitMargin.toFixed(2) + '%',
    totalFees: aggregated.totalFees,
    totalNetFees: aggregated.totalNetFees,
  });

  const formatCurrency = (value) => {
    return `$${value.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  };

  const formatPercent = (value) => {
    return `${value.toFixed(2)}%`;
  };

  return (
    <div className="space-y-6 mb-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h2 className="text-2xl font-bold text-slate-900">Proposal Summary</h2>
      </div>

      {/* Stacked Layout: Gross Fees ABOVE Net Fees */}
      <div className="space-y-6">
        {/* TOP: Gross Fees (Combined) */}
        <div className="card p-6 bg-white border-2 border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4 pb-2 border-b-2 border-blue-500">
            Gross Fees (Combined)
          </h3>

          <div className="space-y-3">
            <div className="flex justify-between items-center py-2">
              <span className="text-slate-600">Total Payments</span>
              <span className="text-slate-900 font-bold">{formatCurrency(aggregated.totalPayments)}</span>
            </div>

            <div className="flex justify-between items-center py-2">
              <span className="text-slate-600">Total Fees</span>
              <span className="text-slate-900 font-bold">{formatCurrency(aggregated.totalFees)}</span>
            </div>

            <div className="flex justify-between items-center py-2">
              <span className="text-slate-600">Total Collections</span>
              <span className="text-slate-900 font-bold">{formatCurrency(aggregated.totalCollections)}</span>
            </div>

            <div className="flex justify-between items-center py-2 bg-blue-50 px-3 rounded">
              <span className="text-blue-900 font-medium">Average Implied TR</span>
              <span className="text-blue-900 font-bold">{formatPercent(avgImpliedTR)}</span>
            </div>

            <div className="flex justify-between items-center py-2">
              <span className="text-slate-600">Merchant Fees</span>
              <span className="text-red-600 font-bold">{formatCurrency(aggregated.totalMerchantFees)}</span>
            </div>

            <div className="flex justify-between items-center py-3 border-t-2 border-emerald-200 bg-emerald-50 px-3 rounded">
              <span className="text-emerald-900 font-bold">Gross Profit</span>
              <span className="text-emerald-600 font-bold text-lg">{formatCurrency(grossProfit)}</span>
            </div>
          </div>
        </div>

        {/* BOTTOM: Net Fees (Combined) */}
        <div className="card p-6 bg-white border-2 border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4 pb-2 border-b-2 border-red-500">
            Net Fees (Combined)
          </h3>

          <div className="space-y-3">
            <div className="flex justify-between items-center py-2">
              <span className="text-slate-600">Reader Costs</span>
              <span className="text-slate-900 font-bold">{formatCurrency(totalReaderCosts)}</span>
            </div>

            <div className="flex justify-between items-center py-2">
              <span className="text-slate-600">Sales Commissions</span>
              <span className="text-slate-900 font-bold">{formatCurrency(aggregated.totalSalesCommissions)}</span>
            </div>

            <div className="flex justify-between items-center py-2">
              <span className="text-slate-600">Partnership Incentive</span>
              <span className="text-slate-900 font-bold">{formatCurrency(aggregated.totalPartnershipIncentive)}</span>
            </div>

            <div className="flex justify-between items-center py-3 border-t-2 border-emerald-200 bg-emerald-50 px-3 rounded">
              <span className="text-emerald-900 font-bold">Final Net Fees</span>
              <span className="text-emerald-600 font-bold text-lg">{formatCurrency(aggregated.totalNetFees)}</span>
            </div>

            <div className="flex justify-between items-center py-2 bg-slate-50 px-3 rounded">
              <span className="text-slate-700 font-medium">Average GPM</span>
              <span className="text-slate-900 font-bold">{formatPercent(avgGPM)}</span>
            </div>

            <div className="flex justify-between items-center py-2 bg-purple-50 px-3 rounded">
              <span className="text-purple-700 font-medium">Average Net Profit Margin</span>
              <span className="text-purple-900 font-bold">{formatPercent(avgNetProfitMargin)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Services in Proposal */}
      <div className="card p-6 bg-white border-2 border-slate-200">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-slate-900">Services in Proposal</h3>
          <span className="text-sm text-slate-600">{normalizedScenarios.length} total</span>
        </div>

        <div className="space-y-3">
          {normalizedScenarios.map((scenario, index) => {
            const totals = scenario.profitability_results?.totals || {};
            const netFees = scenario.profitability_results?.net_fees || {};
            const serviceName = scenario.service_name || scenario.scenario_info?.service || scenario.inputs?.scenario_info?.service || 'Unnamed Service';
            const serviceType = scenario.scenario_info?.serviceType || scenario.inputs?.scenario_info?.serviceType || 'Utilities';
            
            // Extract proposed pricing details
            const proposedPricing = scenario.inputs?.proposed_pricing || scenario.proposed_pricing || {};
            const paymentRates = proposedPricing.paymentRates || {};

            return (
              <div key={scenario.id || index} className="p-4 bg-gradient-to-br from-slate-50 to-white rounded-lg border border-slate-200 hover:border-sky-300 hover:shadow-md transition-all duration-200 group">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-indigo-600 to-indigo-700 text-white text-sm font-bold shadow-sm">
                        #{index + 1}
                      </span>
                      <div>
                        <h4 className="font-bold text-slate-900 group-hover:text-indigo-700 transition-colors">{serviceName}</h4>
                        <p className="text-xs text-slate-500">{serviceType}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg flex items-center gap-1.5 transition-all duration-200 hover:shadow-sm">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 mb-3">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Total Fees</p>
                    <p className="font-bold text-slate-900">{formatCurrency(totals.total_fees || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Net Revenue</p>
                    <p className="font-bold text-emerald-600">{formatCurrency(netFees.net_fees || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Implied TR</p>
                    <p className="font-bold text-blue-600">{formatPercent(totals.implied_tr || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">GPM</p>
                    <p className="font-bold text-slate-900">{formatPercent(totals.gpm || 0)}</p>
                  </div>
                </div>

                {/* Proposed Fee Rates & Minimums Table */}
                {Object.keys(paymentRates).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <h5 className="text-xs font-semibold text-slate-700 mb-2">Proposed Fee Rates & Minimums</h5>
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      {['ACH', 'CC', 'AMEX', 'DEBIT'].map(paymentType => {
                        const rate = paymentRates[paymentType];
                        if (!rate) return null;
                        
                        return (
                          <div key={paymentType} className="bg-white p-2 rounded border border-slate-200">
                            <p className="font-bold text-indigo-700 mb-1">{paymentType}</p>
                            <p className="text-slate-600">
                              <span className="font-medium">Rate:</span> {rate.takeRate ? `${rate.takeRate}%` : 'N/A'}
                            </p>
                            <p className="text-slate-600">
                              <span className="font-medium">Min:</span> {rate.minFee ? formatCurrency(rate.minFee) : 'N/A'}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
